// client/src/pages/org/OrgPropertiesPage.tsx
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle, Plus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { QueryStateBoundary } from '@/components/ui/query-state-boundary';
import { ResponsiveTable, type ResponsiveColumn } from '@/components/ui/responsive-table';
import { Skeleton } from '@/components/ui/skeleton';
import { useOrgContext } from '@/hooks/useOrgContext';
import { extractApiErrorMessage } from '@/lib/api-error';
import {
  createOrgProperty,
  invitePropertyManager,
  listOrgProperties,
  updatePropertyConfig,
} from '@/services/api.client';
import type { OrgProperty } from '@/types';
import { formatDate } from '@/lib/utils';

type RegistrationMode = 'manager_initiated' | 'self_registration';

interface ModeMutation {
  isPending: boolean;
  mutate: (vars: { propertyId: string; registrationMode: RegistrationMode }) => void;
}

/** Columns for the org properties table. Takes the mode mutation so the
 *  per-row registration-mode select can dispatch updates. */
function buildPropertyColumns(modeMutation: ModeMutation): ResponsiveColumn<OrgProperty>[] {
  return [
    {
      key: 'name',
      header: 'Name',
      priority: 'primary',
      cell: (p) => <span className="font-medium">{p.name}</span>,
    },
    { key: 'address', header: 'Address', priority: 'secondary', cell: (p) => p.address },
    {
      key: 'type',
      header: 'Type',
      priority: 'secondary',
      cell: (p) => <span className="capitalize">{p.propertyType.replace(/_/g, ' ')}</span>,
    },
    { key: 'subdomain', header: 'Subdomain', priority: 'secondary', cell: (p) => p.subdomain },
    {
      key: 'registration',
      header: 'Registration',
      priority: 'secondary',
      cell: (p) => (
        <select
          aria-label={`Registration mode for ${p.name}`}
          value={p.registrationMode}
          disabled={modeMutation.isPending}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) =>
            modeMutation.mutate({
              propertyId: p.id,
              registrationMode: e.target.value as RegistrationMode,
            })
          }
          className="h-8 rounded-md border border-input bg-background px-2 text-xs"
        >
          <option value="manager_initiated">Invite only</option>
          <option value="self_registration">Self-registration</option>
        </select>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      priority: 'primary',
      cell: (p) => <span className="capitalize">{p.status}</span>,
    },
    {
      key: 'created',
      header: 'Created',
      priority: 'secondary',
      cell: (p) => formatDate(p.createdAt),
    },
  ];
}

const PROPERTY_TYPES = [
  {
    value: 'gated_community',
    label: 'Residential — Gated Community',
    hint: 'Walled estate; perimeter gate; hosts are residents',
  },
  {
    value: 'residential_apartment',
    label: 'Residential — Apartment Complex',
    hint: 'High-rise or block of flats; hosts are residents',
  },
  {
    value: 'master_planned_city',
    label: 'Residential — Master-Planned City',
    hint: 'Multi-precinct development; hosts are residents',
  },
  {
    value: 'enterprise_building_single_tenant',
    label: 'Enterprise — Single Company',
    hint: 'One company occupies the entire building; hosts are employees',
  },
  {
    value: 'enterprise_building_multi_tenant',
    label: 'Enterprise — Multiple Companies',
    hint: 'Office tower or business park; visitors routed to one of N companies',
  },
  {
    value: 'mixed_use',
    label: 'Mixed-Use',
    hint: 'Both residents and companies share the gates (e.g. condo with retail)',
  },
  { value: 'other', label: 'Other', hint: 'Use sparingly — surfaces a generic flow' },
] as const;

const propertySchema = z.object({
  name: z.string().min(1, 'Property name is required'),
  address: z.string().min(1, 'Address is required'),
  propertyType: z.enum([
    'residential_apartment',
    'gated_community',
    'master_planned_city',
    'enterprise_building_single_tenant',
    'enterprise_building_multi_tenant',
    'mixed_use',
    'other',
  ]),
  subdomain: z.string().min(3, 'Subdomain must be at least 3 characters'),
});
type PropertyFormValues = z.infer<typeof propertySchema>;

const pmSchema = z.object({
  fullName: z.string().min(2, 'Full name is required'),
  email: z.string().email('Enter a valid email'),
  phone: z.string().min(9, 'Enter a valid phone number'),
});
type PmFormValues = z.infer<typeof pmSchema>;

type WizardStep = 'create' | 'invite-pm';

export function OrgPropertiesPage() {
  const ctx = useOrgContext();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<WizardStep>('create');
  const [createdPropertyId, setCreatedPropertyId] = useState<string | null>(null);

  const propertiesQuery = useQuery({
    queryKey: ['org-properties', ctx?.organisationId],
    queryFn: () => listOrgProperties(ctx?.organisationId ?? ''),
    enabled: !!ctx,
  });

  // ── Step 1: Create Property ──────────────────────────────────────────────

  const propForm = useForm<PropertyFormValues>({
    resolver: zodResolver(propertySchema),
    defaultValues: { propertyType: 'gated_community' },
  });

  const createMut = useMutation({
    mutationFn: (values: PropertyFormValues) =>
      createOrgProperty(ctx?.organisationId ?? '', values),
    onSuccess: (property) => {
      void qc.invalidateQueries({ queryKey: ['org-properties', ctx?.organisationId] });
      toast.success('Property created');
      setCreatedPropertyId(property.id);
      setStep('invite-pm');
    },
    onError: (err) => toast.error(extractApiErrorMessage(err, 'Failed to create property')),
  });

  // ── Step 2: Invite PM ────────────────────────────────────────────────────

  const pmForm = useForm<PmFormValues>({ resolver: zodResolver(pmSchema) });

  const inviteMut = useMutation({
    mutationFn: (values: PmFormValues) => {
      if (!createdPropertyId) throw new Error('No property created yet');
      return invitePropertyManager(createdPropertyId, {
        fullName: values.fullName,
        email: values.email,
        phone: values.phone,
      });
    },
    onSuccess: () => {
      toast.success('Property manager invitation sent');
      closeWizard();
    },
    onError: (err) => toast.error(extractApiErrorMessage(err, 'Failed to invite property manager')),
  });

  const closeWizard = () => {
    setOpen(false);
    setStep('create');
    setCreatedPropertyId(null);
    propForm.reset();
    pmForm.reset();
  };

  // ── Per-row registration-mode toggle (independent of the wizard) ─────────

  const modeMutation = useMutation({
    mutationFn: ({
      propertyId,
      registrationMode,
    }: {
      propertyId: string;
      registrationMode: 'manager_initiated' | 'self_registration';
    }) => updatePropertyConfig(propertyId, { registrationMode }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['org-properties', ctx?.organisationId] });
      toast.success('Registration mode updated');
    },
    onError: () => {
      toast.error('Failed to update registration mode');
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl">Properties</h1>
        <Button onClick={() => setOpen(true)}>
          <Plus className="size-4 mr-2" />
          Create Property
        </Button>
      </div>

      <QueryStateBoundary
        query={propertiesQuery}
        errorTitle="Couldn't load properties"
        errorFallback="The properties list is unavailable. Try again in a moment."
        loadingFallback={<Skeleton className="h-40 w-full" />}
        emptyMessage="No properties yet"
      >
        {(properties) => (
          <ResponsiveTable
            columns={buildPropertyColumns(modeMutation)}
            rows={properties}
            getRowKey={(p) => p.id}
            caption="Organisation properties"
          />
        )}
      </QueryStateBoundary>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) closeWizard();
          else setOpen(true);
        }}
      >
        <DialogContent aria-describedby={undefined}>
          {step === 'create' && (
            <>
              <DialogHeader>
                <DialogTitle>Create Property</DialogTitle>
              </DialogHeader>
              <form onSubmit={propForm.handleSubmit((v) => createMut.mutate(v))}>
                <FieldGroup className="py-2">
                  <Field>
                    <FieldLabel htmlFor="name">Property Name</FieldLabel>
                    <Input id="name" {...propForm.register('name')} placeholder="Runda Estate" />
                    <FieldError errors={[propForm.formState.errors.name]} />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="address">Address</FieldLabel>
                    <Input
                      id="address"
                      {...propForm.register('address')}
                      placeholder="Runda, Nairobi, Kenya"
                    />
                    <FieldError errors={[propForm.formState.errors.address]} />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="propertyType">Property Type</FieldLabel>
                    <select
                      id="propertyType"
                      {...propForm.register('propertyType')}
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                    >
                      {PROPERTY_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                    {(() => {
                      const selected = PROPERTY_TYPES.find(
                        (t) => t.value === propForm.watch('propertyType'),
                      );
                      return selected ? (
                        <p className="text-xs text-muted-foreground mt-1">{selected.hint}</p>
                      ) : null;
                    })()}
                    <FieldError errors={[propForm.formState.errors.propertyType]} />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="subdomain">Subdomain</FieldLabel>
                    <Input id="subdomain" {...propForm.register('subdomain')} placeholder="runda" />
                    <FieldError errors={[propForm.formState.errors.subdomain]} />
                  </Field>
                </FieldGroup>
                <DialogFooter className="mt-4">
                  <Button type="button" variant="outline" onClick={closeWizard}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createMut.isPending}>
                    {createMut.isPending ? 'Creating…' : 'Create & Continue'}
                  </Button>
                </DialogFooter>
              </form>
            </>
          )}

          {step === 'invite-pm' && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CheckCircle className="size-5 text-[var(--status-green-text)]" />
                  Invite Property Manager
                </DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Your property is ready. Invite a manager to take over operations, or skip and do
                  it later.
                </p>
              </DialogHeader>
              <form onSubmit={pmForm.handleSubmit((v) => inviteMut.mutate(v))}>
                <FieldGroup className="py-2">
                  <Field>
                    <FieldLabel htmlFor="fullName">Full Name</FieldLabel>
                    <Input
                      id="fullName"
                      {...pmForm.register('fullName')}
                      placeholder="Jane Kamau"
                    />
                    <FieldError errors={[pmForm.formState.errors.fullName]} />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="email">Email</FieldLabel>
                    <Input
                      id="email"
                      type="email"
                      {...pmForm.register('email')}
                      placeholder="jane@example.com"
                    />
                    <FieldError errors={[pmForm.formState.errors.email]} />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="phone">Phone</FieldLabel>
                    <Input
                      id="phone"
                      type="tel"
                      {...pmForm.register('phone')}
                      placeholder="+254 700 000 000"
                    />
                    <FieldError errors={[pmForm.formState.errors.phone]} />
                  </Field>
                </FieldGroup>
                <DialogFooter className="mt-4">
                  <Button type="button" variant="outline" onClick={closeWizard}>
                    Skip for now
                  </Button>
                  <Button type="submit" disabled={inviteMut.isPending}>
                    {inviteMut.isPending ? 'Inviting…' : 'Send Invitation'}
                  </Button>
                </DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
